import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { 
  MagnifyingGlassIcon,
  FunnelIcon,
  StarIcon,
  ClockIcon,
  HeartIcon
} from '@heroicons/react/24/outline';
import { ProductCategory, ProductFilters } from '../types';
import { formatCurrency } from '../utils';
import { CATEGORY_LABELS, SECTOR_LABELS } from '../types';
import { productService, Product } from '../services/productService';

export const MarketplacePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ProductFilters>({
    search: searchParams.get('search') || '',
    category: (searchParams.get('category') as ProductCategory) || undefined,
    sector: (searchParams.get('sector') as any) || undefined,
    minPrice: searchParams.get('minPrice') ? Number(searchParams.get('minPrice')) : undefined,
    maxPrice: searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) : undefined,
    sortBy: 'newest',
  });
  const [showFilters, setShowFilters] = useState(false);

  // Fetch products from API
  useEffect(() => {
    const loadProducts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Build filters object, excluding undefined values
        const apiFilters: {
          category?: string;
          sector?: string;
          minPrice?: number;
          maxPrice?: number;
          search?: string;
          status: string;
        } = {
          status: 'APPROVED', // Only fetch approved products
        };

        if (filters.category) {
          apiFilters.category = filters.category;
        }
        if (filters.sector) {
          apiFilters.sector = filters.sector;
        }
        if (filters.minPrice !== undefined && filters.minPrice !== null) {
          apiFilters.minPrice = filters.minPrice;
        }
        if (filters.maxPrice !== undefined && filters.maxPrice !== null) {
          apiFilters.maxPrice = filters.maxPrice;
        }
        if (filters.search && filters.search.trim()) {
          apiFilters.search = filters.search.trim();
        }
        
        const fetchedProducts = await productService.getAllProducts(apiFilters);
        setProducts(fetchedProducts);
      } catch (err: any) {
        console.error('Error loading products:', err);
        setError(err.response?.data?.message || 'Failed to load products. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProducts();
  }, [filters.category, filters.sector, filters.minPrice, filters.maxPrice, filters.search]);

  // Filter and sort products (API already filters by status, but we ensure only APPROVED here)
  const filteredProducts = useMemo(() => {
    let filtered = products.filter((product) => product.status === 'APPROVED');

    // Sort (API handles category, sector, price, and search filters)
    switch (filters.sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
      case 'price_low':
        filtered.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));
        break;
      case 'price_high':
        filtered.sort((a, b) => parseFloat(b.price) - parseFloat(a.price));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'downloads':
        filtered.sort((a, b) => (b.downloads || 0) - (a.downloads || 0));
        break;
    }

    return filtered;
  }, [products, filters.sortBy]);

  const handleFilterChange = (key: keyof ProductFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: undefined,
      sector: undefined,
      minPrice: undefined,
      maxPrice: undefined,
      sortBy: 'newest',
    });
  };

  const categoryOptions = Object.entries(CATEGORY_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  const sectorOptions = Object.entries(SECTOR_LABELS).map(([value, label]) => ({
    value,
    label,
  }));

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'downloads', label: 'Most Downloaded' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 animate-fadeIn">
        <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-secondary-900 mb-2">
          Marketplace
        </h1>
        <p className="font-body text-lg text-secondary-600">
          Discover quality care documents from trusted professionals
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200 p-6 mb-8">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <Input
              type="text"
              placeholder="Search for documents..."
              value={filters.search || ''}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              leftIcon={<MagnifyingGlassIcon />}
            />
          </div>

          {/* Sort */}
          <div className="lg:w-48">
            <Select
              value={filters.sortBy || 'newest'}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('sortBy', e.target.value)}
              options={sortOptions}
            />
          </div>

          {/* Filter Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="lg:w-auto"
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
          </Button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-secondary-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Select
                label="Category"
                value={filters.category || ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('category', e.target.value || undefined)}
                options={[{ value: '', label: 'All Categories' }, ...categoryOptions]}
                placeholder="Select category"
              />

              <Select
                label="Sector"
                value={filters.sector || ''}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterChange('sector', e.target.value || undefined)}
                options={[{ value: '', label: 'All Sectors' }, ...sectorOptions]}
                placeholder="Select sector"
              />

              <Input
                label="Min Price (£)"
                type="number"
                value={filters.minPrice || ''}
                onChange={(e) => handleFilterChange('minPrice', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="0"
              />

              <Input
                label="Max Price (£)"
                type="number"
                value={filters.maxPrice || ''}
                onChange={(e) => handleFilterChange('maxPrice', e.target.value ? Number(e.target.value) : undefined)}
                placeholder="100"
              />
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="ghost" onClick={clearFilters}>
                Clear Filters
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-secondary-600">
          {isLoading ? 'Loading...' : `${filteredProducts.length} product${filteredProducts.length !== 1 ? 's' : ''} found`}
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-secondary-600">Loading products...</p>
          </div>
        </div>
      ) : filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => {
            // Get image URL - prefer signed URL from backend
            const getImageUrl = (): string => {
              if (product.thumbnailUrl) return product.thumbnailUrl;
              const thumbnailMedia = product.media?.find(m => m.entityType === 'PRODUCT_THUMBNAIL');
              if (thumbnailMedia?.signedUrl) return thumbnailMedia.signedUrl;
              if (thumbnailMedia?.fileUrl) return thumbnailMedia.fileUrl;
              if (product.thumbnail) {
                return `https://markteplacemedia.s3.eu-north-1.amazonaws.com/${product.thumbnail}`;
              }
              return 'https://via.placeholder.com/400x300?text=No+Image';
            };

            const reviewCount = product._count?.reviews || product.reviewCount || 0;
            const rating = product.rating || 0;

            return (
              <Card key={product.id} hover className="group">
                <Link to={`/product/${product.id}`}>
                  <div className="aspect-w-16 aspect-h-9 mb-4">
                    <img
                      src={getImageUrl()}
                      alt={product.title}
                      className="w-full h-48 object-cover rounded-lg"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=No+Image';
                      }}
                    />
                  </div>
                </Link>
                
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <Badge variant="primary" size="sm">
                      {CATEGORY_LABELS[product.category as ProductCategory] || product.category}
                    </Badge>
                    <button className="p-1 text-secondary-400 hover:text-red-500 transition-colors">
                      <HeartIcon className="h-4 w-4" />
                    </button>
                  </div>
                  
                  <div>
                    <Link to={`/product/${product.id}`}>
                      <h3 className="font-semibold text-secondary-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {product.title}
                      </h3>
                    </Link>
                    <p className="text-sm text-secondary-600 mt-1 line-clamp-2">
                      {product.description}
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm text-secondary-500">
                    <div className="flex items-center">
                      <StarIcon className="h-4 w-4 text-yellow-400 mr-1" />
                      {rating.toFixed(1)} ({reviewCount})
                    </div>
                    <div className="flex items-center">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      {product.downloads || 0}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-lg font-bold text-primary-600">
                      {formatCurrency(parseFloat(product.price))}
                    </div>
                    <Link to={`/product/${product.id}`}>
                      <Button size="sm">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-secondary-400 mb-4">
            <MagnifyingGlassIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-secondary-900 mb-2">
            No products found
          </h3>
          <p className="text-secondary-600 mb-4">
            {filters.search || filters.category || filters.sector
              ? 'Try adjusting your search criteria or browse all products'
              : 'No approved products available at the moment. Check back soon!'}
          </p>
          {(filters.search || filters.category || filters.sector) && (
            <Button onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
